import { z } from 'zod';
import { NOTIFICATION_CHANNELS, NOTIFICATION_TYPES } from '../enums';
import { richText, uuid } from './common';

export const createCommentSchema = z.object({
  body: richText,
  parentCommentId: uuid.nullable().optional(),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;

export const updateCommentSchema = z.object({ body: richText });
export type UpdateCommentInput = z.infer<typeof updateCommentSchema>;

export const reactionSchema = z.object({ emoji: z.string().min(1).max(16) });

export const notificationPrefsSchema = z.object({
  prefs: z.array(
    z.object({
      eventType: z.enum(NOTIFICATION_TYPES),
      channels: z.array(z.enum(NOTIFICATION_CHANNELS)),
    }),
  ),
});
export type NotificationPrefsInput = z.infer<typeof notificationPrefsSchema>;

export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string(), auth: z.string() }),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

// ---------------------------------------------------------------------------------------------
// Channels, DMs, messages (FR-MSG-3..8, 11)
// ---------------------------------------------------------------------------------------------

export const createChannelSchema = z.object({
  projectKey: z.string(),
  name: z.string().min(1).max(60).regex(/^[\w\- ]+$/, 'Letters, numbers, spaces, - and _'),
  type: z.enum(['custom', 'qa', 'team']).default('custom'),
  topic: z.string().max(250).optional(),
  isPrivate: z.boolean().default(false),
  memberIds: z.array(uuid).max(200).default([]),
});
export const updateChannelSchema = z.object({ name: z.string().min(1).max(60).optional(), topic: z.string().max(250).nullable().optional(), archived: z.boolean().optional() });
export const channelMembersSchema = z.object({ add: z.array(uuid).max(200).default([]), remove: z.array(uuid).max(200).default([]) });
export const dmSchema = z.object({ userIds: z.array(uuid).min(1).max(8) });
export const postMessageSchema = z.object({ body: richText, threadParentId: uuid.nullable().optional() });
export const editMessageSchema = z.object({ body: richText });
export const messageToItemSchema = z.object({ projectKey: z.string(), type: z.enum(['task', 'bug', 'story', 'quick_task']).default('task'), title: z.string().min(1).max(255).optional() });
export const threadToItemSchema = z.object({ itemKey: z.string() });

// ---------------------------------------------------------------------------------------------
// Stand-ups (FR-MSG-9, FR-NOTE-15) and pages (FR-MSG-10)
// ---------------------------------------------------------------------------------------------

export const standupSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  projectKey: z.string().optional(),
  yesterday: z.string().max(4000).default(''),
  today: z.string().max(4000).default(''),
  blockers: z.string().max(4000).default(''),
});
export type StandupInput = z.infer<typeof standupSchema>;

export const pageSchema = z.object({
  title: z.string().min(1).max(200),
  content: richText.optional(),
  parentId: uuid.nullable().optional(),
  linkedReleaseId: uuid.nullable().optional(),
  template: z.enum(['blank', 'release_notes', 'test_strategy', 'runbook', 'decision']).default('blank'),
  itemKeys: z.array(z.string()).max(100).optional(),
});
export type PageInput = z.infer<typeof pageSchema>;
export const updatePageSchema = pageSchema.partial().omit({ template: true }).extend({ version: z.number().int().min(1) });
export type UpdatePageInput = z.infer<typeof updatePageSchema>;
